import { type ReactNode, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import type { AvatarData } from '@/api/gen/_model/avatarData';
import { useViewport } from '@/hooks';
import { ResetIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { AnimatedAvatar } from '@/shared/features/user-avatar';
import { Button, confirmDialog, DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { AvatarPartSelector } from './components/AvatarPartSelector';
import { AvatarPreviewPanel } from './components/AvatarPreviewPanel';
import { AvatarSavedToast } from './components/AvatarSavedToast';
import { MyPointsChip } from './components/MyPointsChip';
import { RandomAvatarButton } from './components/RandomAvatarButton';
import { type AvatarSaveStrategy } from './hooks/types';
import { type AvatarEventSource } from './events.quiz-profile-avatar';
import { type AvatarDecorateForm, AvatarDecorateSchema } from './schema';
interface AvatarDecorateModalProps {
  /** 현재 아바타 (없으면 기본 파츠). */
  initialAvatar?: AvatarData | null;
  onClose: () => void;
  /** 저장 전략 주입: /profile/edit=프로필 DB, play=게임 SET_AVATAR. */
  onSave: AvatarSaveStrategy['save'];
  /** 모달 진입 위치. 저장·배너 클릭 이벤트와 동일한 값으로 연결한다. */
  source: AvatarEventSource;
  isSaving?: boolean;
  /**
   * 젤리·클래스 안내를 얹는 자리(PRD §7). 플레이에서 학생이 열었을 때만 채운다.
   *
   * 모달이 아니라 **인라인**이다. 1회 게이트 없이 조건이 유지되는 동안 계속 뜨므로
   * 작아야 견딜 수 있다(§5). 데스크탑은 우측 프리뷰 패널 위, 모바일은 파츠 목록 위에
   * 놓아 아바타를 가리지 않게 한다.
   */
  notice?: ReactNode;
  /**
   * 젤리 배너를 눌렀을 때(교사만 보인다). 넘기면 배너가 링크가 아니라 버튼이 되어
   * 화면을 떠나지 않는다 — 플레이에서 연 경우 그 자리에서 지급 모달을 연다.
   * 넘기지 않으면 기존대로 내 클래스로 이동한다(프로필 편집).
   */
  onGrantBannerClick?: () => void;
  /** 젤리를 줄 수 있는 사람인지. 못 주면 지급 배너를 띄우지 않는다. */
  canGrantJelly?: boolean;
  /** 우하단 젤리 안내 팝오버. 퀴즈당 1회 노출 판단은 호출부가 한다. */
  jellyPopover?: ReactNode;
}
const toFormValue = (avatar?: AvatarData | null): AvatarDecorateForm => ({
  avatar: {
    skinId: avatar?.skinId ?? 1,
    faceId: avatar?.faceId ?? 1,
    hairId: avatar?.hairId ?? 1,
    clothesId: avatar?.clothesId ?? 1,
    accessoryIds: avatar?.accessoryIds as Record<string, number> ?? {}
  }
});
const getPendingAvatarStorageKey = (source: AvatarEventSource) => `zep.quiz.pendingAvatarDecorate.${source}`;
const readPendingAvatar = (source: AvatarEventSource): AvatarDecorateForm | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(getPendingAvatarStorageKey(source));
    if (!raw) return null;
    window.sessionStorage.removeItem(getPendingAvatarStorageKey(source));
    const parsed = JSON.parse(raw) as AvatarDecorateForm;
    return AvatarDecorateSchema.parse(parsed);
  } catch {
    return null;
  }
};
const writePendingAvatar = (source: AvatarEventSource, avatar: AvatarDecorateForm) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(getPendingAvatarStorageKey(source), JSON.stringify(avatar));
  } catch {
    // best-effort. 실패해도 기존 로그인 플로우는 유지한다.
  }
};
const clearPendingAvatar = (source: AvatarEventSource) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(getPendingAvatarStorageKey(source));
  } catch {
    // best-effort.
  }
};
function RestoreSavedAvatarIconButton({
  onReset,
  disabled,
  ariaLabel
}: {
  onReset: () => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  return <IconButton variant="outlined" intent="neutral" size="md" rounded="default" disabled={disabled} aria-label={ariaLabel} onClick={onReset} data-sentry-element="IconButton" data-sentry-component="RestoreSavedAvatarIconButton" data-sentry-source-file="AvatarDecorateModal.tsx">
      <ResetIcon size="sm" color={disabled ? ICON_COLOR.disabled : ICON_COLOR.neutral} data-sentry-element="ResetIcon" data-sentry-source-file="AvatarDecorateModal.tsx" />
    </IconButton>;
}
function MobileAvatarPreview() {
  const {
    watch
  } = useFormContext<AvatarDecorateForm>();
  const avatar = watch('avatar');
  return <div className="flex h-[100px] w-full shrink-0 items-start justify-center pt-none" data-sentry-component="MobileAvatarPreview" data-sentry-source-file="AvatarDecorateModal.tsx">
      <div className="flex h-full flex-1 items-center justify-center px-md">
        <div className="flex h-[100px] w-[86px] shrink-0 items-center justify-center overflow-hidden">
          <AnimatedAvatar avatarData={avatar} behavior="stand" className="origin-center scale-[1.5]" data-sentry-element="AnimatedAvatar" data-sentry-source-file="AvatarDecorateModal.tsx" />
        </div>
      </div>
    </div>;
}
export const AvatarDecorateModal = ({
  initialAvatar,
  onClose,
  onSave,
  source,
  isSaving,
  notice,
  onGrantBannerClick,
  canGrantJelly,
  jellyPopover
}: AvatarDecorateModalProps) => {
  const {
    t
  } = useTranslation('common');
  const {
    isAuthenticated
  } = useUser();
  const router = useRouter();
  const {
    isMobile
  } = useViewport();
  const form = useForm<AvatarDecorateForm>({
    resolver: zodResolver(AvatarDecorateSchema),
    defaultValues: toFormValue(initialAvatar)
  });
  useEffect(() => {
    if (!isAuthenticated || router.query.avatar_login !== '1' || router.query.avatar_login_source !== source) {
      return;
    }
    const pendingAvatar = readPendingAvatar(source);
    if (!pendingAvatar) return;
    form.reset(pendingAvatar);
    form.setValue('avatar', pendingAvatar.avatar, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  }, [form, isAuthenticated, router.query.avatar_login, router.query.avatar_login_source, source]);
  useEffect(() => {
    const isReturningFromAvatarLogin = isAuthenticated && router.query.avatar_login === '1' && router.query.avatar_login_source === source;

    // 로그인 복귀 시에는 sessionStorage에 저장한 편집본이 우선이다. 그 외에는 아직
    // 편집하지 않은 모달만 늦게 도착한 플레이 세션 아바타를 받아들인다.
    if (isReturningFromAvatarLogin || form.formState.isDirty) return;
    form.reset(toFormValue(initialAvatar));
  }, [form, form.formState.isDirty, initialAvatar, isAuthenticated, router.query.avatar_login, router.query.avatar_login_source, source]);

  // 모달은 열릴 때마다 새로 mount 되므로 실제 노출 1회만 발화한다.
  useEffect(() => {
    trackEvent('quiz_profile_avatar_dialog_view', {
      source,
      login_state: isAuthenticated ? 'authenticated' : 'guest'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSave = form.handleSubmit(async ({
    avatar
  }) => {
    // 게스트에게 로그인을 권하는 자리는 여기다(시안 1811:89530).
    //
    // 예전에는 유료 아이템을 누를 때 물었는데, 그건 게스트가 잃는 것과 어긋난다 —
    // 꾸미는 것 자체는 잘 되고, 사라지는 건 **저장한 결과**다. 그래서 저장을 누른
    // 순간에만 말하고, 「넘어가기」면 그대로 저장을 진행한다(이번 접속에는 남는다).
    if (!isAuthenticated) {
      trackEvent('quiz_profile_avatar_save_login_required_view', {
        source
      });
      const shouldLogin = await confirmDialog.openAsync({
        size: 'xs',
        forceDrawerOnMobile: false,
        className: 'w-[calc(100vw-32px)] max-w-[343px]',
        footerClassName: '!flex-col-reverse',
        // 바깥 클릭은 사용자의 「나중에 할래요」 선택이 아니다. 기본값(true)대로
        // 닫으면 openAsync가 false를 돌려 주고 아래 게스트 저장 흐름이 그대로 실행돼
        // 아바타 꾸미기 모달이 의도치 않게 적용된다.
        shouldCloseOnDimClick: false,
        header: {
          title: t('guestSave.title')
        },
        // 제목은 요구, 본문은 「넘어가기」의 결과를 맡는다. "저장이 안 돼요" 가 아니라
        // "다시 들어오면 사라져요" 인 이유는, 지금 화면에서는 아바타가 멀쩡히 바뀌어
        // 있어서 무엇을 잃는지가 눈에 안 보이기 때문이다.
        body: t('guestSave.body'),
        cancelText: t('guestSave.skip'),
        confirmText: t('avatarDecorate.loginAction')
      });
      trackEvent('quiz_profile_avatar_save_login_required_click', {
        source,
        destination: shouldLogin ? 'login' : 'dismiss'
      });
      if (shouldLogin) {
        trackEvent('quiz_profile_avatar_login_click', {
          source
        });
        writePendingAvatar(source, {
          avatar
        });
        const callbackUrl = new URL(router.asPath, window.location.origin);
        callbackUrl.searchParams.set('avatar_login', '1');
        callbackUrl.searchParams.set('avatar_login_source', source);
        void router.push({
          pathname: '/login',
          query: {
            callbackUrl: `${callbackUrl.pathname}${callbackUrl.search}`
          }
        });
        return;
      }
      // 넘어가기 — 아래 저장을 그대로 태운다. 게스트 저장은 play 런타임에만 반영돼
      // 이번 접속 동안은 유지된다.
    }
    try {
      await onSave(avatar);
      clearPendingAvatar(source);
      onClose();
      // 저장은 모달을 닫으며 끝나서, 닫힘 자체 말고는 성공했다는 신호가 없었다.
      // 모달이 사라진 자리에 한 줄로 알린다 — Toaster 기본이 top-right 라
      // 위치를 명시한다(지급 토스트도 같은 top-center).
      toast.custom(toastInstance => <AvatarSavedToast message={t('avatarDecorate.saveSuccessToast')} isVisible={toastInstance.visible} />, {
        position: 'top-center'
      });
    } catch (error) {
      logger.captureError(error);
    }
  });
  const isDirty = form.formState.isDirty;
  const handleResetToSaved = () => {
    form.reset(toFormValue(initialAvatar));
  };

  // 휴대폰에서만 바텀시트를 쓴다. tabletSm(가로 561~768px)는 물리 해상도·DPR에 따라
  // 스크린샷 폭이 크게 보여도 이 분기에 들어와, 전체 높이 시트가 화면 아래로 쏠렸다.
  // 태블릿은 데스크탑과 같은 중앙 모달을 유지해 프리뷰와 파츠 목록을 함께 보여준다.
  if (isMobile) {
    return <ResponsiveDialog size="lg" className="h-[calc(100dvh-60px)] !max-h-[calc(100dvh-60px)] rounded-t-lg border-0" onClose={onClose}>
        <FormProvider {...form}>
          <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="px-md pb-xs pt-md" />
          <div className="flex shrink-0 items-center justify-between px-md pb-none">
            <div className="flex items-center gap-xs">
              <RestoreSavedAvatarIconButton onReset={handleResetToSaved} disabled={!isDirty} ariaLabel={t('avatarDecorate.restoreSavedAriaLabel')} />
              <RandomAvatarButton iconOnly />
            </div>
            {/* 데스크탑은 우측 프리뷰 패널에 있는 잔액 칩. 모바일은 그 패널을 쓰지 않아 여기 둔다. */}
            <MyPointsChip />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MobileAvatarPreview />
            {Boolean(notice) && <div className="px-md pb-xs">{notice}</div>}
            <div className="min-h-0 flex-1 overflow-hidden">
              <AvatarPartSelector />
            </div>
          </div>
          {Boolean(jellyPopover) && (
        /**
         * 시안(1867:26620)은 팝오버 하단이 액션 바 윗변에 **맞닿아** 있다.
         * 아래 액션 바가 pt-xs(4) + 버튼 40 + pb-md(16) = 60 이므로 그만큼만 띄운다.
         * 80 으로 두었더니 20px 이 떠서 파츠 그리드 위에 어정쩡하게 걸쳐 있었다.
         */
        <div className="pointer-events-none absolute inset-x-0 bottom-[60px] z-10 px-sm">
              <div className="pointer-events-auto">{jellyPopover}</div>
            </div>)}
          <div className="flex shrink-0 items-center justify-center gap-xs bg-default px-md pb-md pt-xs">
            <Button variant="contained" intent="strong" size="md" className="h-[40px] flex-1" disabled={!isDirty || isSaving} onClick={handleSave}>
              {t('avatarDecorate.save')}
            </Button>
          </div>
        </FormProvider>
      </ResponsiveDialog>;
  }
  return <ResponsiveDialog size="xl" className="flex h-[min(640px,calc(100dvh-80px))] !max-h-[640px] mobile-lg:!w-[calc(100vw-80px)] mobile-lg:!max-w-[960px]" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="AvatarDecorateModal" data-sentry-source-file="AvatarDecorateModal.tsx">
      <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-source-file="AvatarDecorateModal.tsx">
        <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="border-b border-assistive" data-sentry-element="DialogHeader" data-sentry-source-file="AvatarDecorateModal.tsx" />
        <div className="flex min-h-0 flex-1 flex-col mobile-lg:flex-row">
          {/*
            태블릿(모달 최대폭 < 960px)에서는 좌측 그룹이 먼저 줄어야 우측 프리뷰(300px)가
            잘리지 않는다. flex-none 이면 660px 를 고집해 우측이 깎였다.
           */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden mobile-lg:w-[660px] mobile-lg:flex-initial">
            <AvatarPartSelector data-sentry-element="AvatarPartSelector" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
          {/*
            Figma(1333:56263): 프리뷰 패널은 min-w 250 / max-w 300 으로 같이 줄어든다.
            shrink-0 로 300px 를 고정하면 축소분이 전부 아이템 그리드로 몰려 1열까지 찌그러진다.
           */}
          <div className="flex shrink-0 flex-col border-assistive mobile-lg:w-[300px] mobile-lg:min-w-[250px] mobile-lg:shrink mobile-lg:border-l">
            {Boolean(notice) && <div className="px-md pt-md">{notice}</div>}
            <AvatarPreviewPanel onSave={handleSave} isSaving={isSaving} onResetToSaved={handleResetToSaved} source={source} popover={jellyPopover} onGrantBannerClick={onGrantBannerClick} canGrantJelly={canGrantJelly} data-sentry-element="AvatarPreviewPanel" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
        </div>
      </FormProvider>
    </ResponsiveDialog>;
};